import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useRef, useMemo } from 'react'

import { useStickyWrapperHeight } from '@src/hooks/useStickyWrapperHeight'
import { useQuinceRouter } from '@src/modules/navigation'
import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import CollectionFilterLoader from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilterLoader'
import { CheckboxGroup, ColorFilterGroup, Icon, RadioButtonGroup } from '@src/quince-components/src/atoms'
import StickyWrapper from '@src/quince-components/src/atoms/stickyWrapper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'

import { FilterCollapseGroup } from '../../components/FilterCollapseGroup'
import { FilterDrawer } from '../../components/FilterDrawer'
import { FilterDropdown } from '../../components/FilterDropdown'
import { FILTER_DROPDOWN_TYPES, SORT_BY_CLP_EXPERIMENT, SORT_OPTIONS_CLP_EXPERIMENT } from '../../constants/constants'
import { FALLBACK_COLOR, FILTER_TYPES } from '../../constants/filterSection.constants'
import { useEnhancedFilterState } from '../../hooks/useEnhancedFilterState'
import { FilterSectionWithDrawerProps, FilterItem, CheckboxOption, UpdateQueryParams } from '../filterSection.interface'
import {
  createCheckboxOptions,
  getDisplayNameForFilterValue,
  getSelectedCount,
  handleRadioFilterChange,
  prepareRadioFilterData,
  toTitleCase,
  transformSubCollectionCards,
} from '../filterUtils'
import FilterInlineIcon from '../icon/filter.inline.svg'

import styles from './mobileFilters.module.scss'
import useMobileFilters from './useMobileFilters'

const FILTER_TEXT = 'filter'
const SORT_BY_TEXT = 'sort_by'
const FILTER_DRAWER_BUTTON_TEST_ID = 'filter-drawer-button'
const SORT_TRIGGER_TEST_ID = 'sort-trigger'
const SORT_DRAWER_TITLE_TEST_ID = 'sort-drawer-title'
const SORT_OPTIONS_MOBILE_DRAWER_NAME = 'sort-options-mobile-drawer'
const CLEAR_ALL_BUTTON_TEST_ID = 'clear-all-button'
const FILTER_CHIP_PREFIX = 'filter-chip'
const COLLECTION_FILTER_LOADER_TEST_ID = 'collection-filter-loader'

export const MobileFilters: React.FC<FilterSectionWithDrawerProps> = props => {
  const { t } = useTranslation('common')
  const {
    className,
    filters: serverFilters = [],
    noOfItems = 0,
    selectedFilters = {},
    sortBy = '',
    hideFilters = false,
    skipSorting = false,
    collectionSlug,
    updateQuery = (_params: UpdateQueryParams) => {
      /* no-op */
    },
    showShimmer = false,
    showGroupedPage = false,
    subCollectionCards,
  } = props

  const router = useQuinceRouter()
  const routerAsPath = router?.asPath?.split('?')[0] ?? ''
  const isSearchMode = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsSearchMode)
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType !== DEVICE_TYPES.DESKTOP

  const typedSubCollectionCards = useMemo(() => transformSubCollectionCards(subCollectionCards), [subCollectionCards])

  const {
    isDrawerOpen,
    isSortDrawerOpen,
    collapseActiveKeys,
    headerHeight,
    isScrollingDown,
    filters,
    shouldApplyTransitions,
    openDrawerWithFilter,
    openSortDrawer,
    closeDrawer,
    closeSortDrawer,
    handleMainFilterClick,
    handleCollapseChange,
  } = useMobileFilters({
    serverFilters,
    collectionSlug,
    showGroupedPage,
  })

  // Use the height calculation hook to prevent CLS when filters become sticky
  const { wrapperRef, containerRef } = useStickyWrapperHeight({
    enabled: isMobile,
    dependencies: [filters.length, isDrawerOpen],
  })

  // Use the enhanced filter state hook with subcollection cards
  const {
    selectedFiltersState,
    selectedFilterChips,
    clearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismissByLabel,
    effectiveTotalSelectedFilters,
  } = useEnhancedFilterState({
    selectedFilters,
    showGroupedPage,
    routerAsPath,
    serverFilters,
    updateQuery,
    subCollectionCards: typedSubCollectionCards,
  })
  const routerContext = useMemo(() => ({ router, routerAsPath }), [router, routerAsPath])
  const commonParams = useMemo(
    () => ({ showGroupedPage, updateQuery, filters, subCollectionCards: typedSubCollectionCards, serverFilters }),
    [showGroupedPage, updateQuery, filters, typedSubCollectionCards, serverFilters],
  )
  const isSortOptionSelected = useRef(sortBy !== SORT_BY_CLP_EXPERIMENT.FEATURED.value)

  // Memoize inline style to avoid creating new objects on every render
  // Use transform instead of top for better performance (compositor-only, no layout/paint)
  const stickyStyle = useMemo(
    () => (headerHeight > 0 ? { transform: `translateY(${headerHeight}px)` } : undefined),
    [headerHeight],
  )

  // If hideFilters is true, don't render anything
  if (hideFilters) {
    return null
  }

  if (showShimmer && !isDrawerOpen && !isSearchMode) {
    return <CollectionFilterLoader data-testid={COLLECTION_FILTER_LOADER_TEST_ID} />
  }

  // Helper function to render a checkbox filter (common between inline and drawer)
  const renderCheckboxFilter = (
    filter: FilterItem,
    options: CheckboxOption[],
    onChange: (selectedOptions: CheckboxOption[]) => void,
    isDrawer = false,
  ) => {
    const selectedCount = getSelectedCount(filter, selectedFiltersState, showGroupedPage)
    const selectedOptions = options.filter(option => option.checked)

    if (isDrawer) {
      const state = selectedFiltersState[filter.name] ?? []
      return (
        <CheckboxGroup
          key={`drawer-${filter.name}-${selectedCount}`}
          options={createCheckboxOptions(
            filter.values,
            state.map(s => s.toString()),
            filter.name,
          )}
          onChange={onChange}
          direction="column"
          maxVisible={8}
        />
      )
    }

    // Format the label for the checkbox filter
    const formatCheckboxFilterLabel = () => {
      const singleSelectedValue =
        selectedCount === 1 && selectedOptions.length === 1 ? selectedOptions[0]?.label : undefined

      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }

    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.CHECKBOX}
        label={formatCheckboxFilterLabel()}
        options={options}
        isActive={selectedCount > 0}
        onChange={onChange}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={() => {
          openDrawerWithFilter(filter.name)
        }}
      />
    )
  }

  // Helper function to render a color filter (common between inline and drawer)
  const renderColorFilter = (filter: FilterItem, isDrawer = false) => {
    const selectedCount = getSelectedCount(filter, selectedFiltersState, showGroupedPage)

    const colorOptions = filter.values.map(value => {
      if (typeof value === 'string') {
        return {
          value,
          hex: FALLBACK_COLOR,
          label: value,
        }
      }

      const colorValue = value
      return {
        value: colorValue.name,
        hex: colorValue.hex ?? FALLBACK_COLOR,
        label: colorValue.displayName ?? colorValue.name,
        imgURL: colorValue.imgURL ?? undefined,
      }
    })

    const handleColorChangeForFilter = (value: string) => {
      handleColorChange(filter, value)
    }

    if (isDrawer) {
      return (
        <ColorFilterGroup
          key={`drawer-color-group-${selectedFiltersState[filter.name]?.length}`}
          options={colorOptions}
          selectedValues={selectedFiltersState[filter.name] ?? []}
          onChange={handleColorChangeForFilter}
          direction="horizontal"
          className={styles.mobileDrawerColorFilterGroup}
          columns={5}
          circleSize={3.6}
          rowGap={0.8}
          columnGap="auto"
        />
      )
    }

    // Format the label for the color filter
    const formatColorFilterLabel = () => {
      let singleSelectedValue: string | undefined
      if (selectedCount === 1 && selectedFiltersState[filter.name]?.length === 1) {
        const selectedValue = selectedFiltersState[filter.name]?.[0]
        if (selectedValue) {
          singleSelectedValue = getDisplayNameForFilterValue({
            filters: serverFilters,
            filterName: filter.name,
            filterValue: selectedValue,
          })
        }
      }
      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }

    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.COLOR}
        label={formatColorFilterLabel()}
        options={colorOptions}
        selectedValues={selectedFiltersState[filter.name] ?? []}
        isActive={(selectedFiltersState[filter.name] ?? []).length > 0}
        onChange={handleColorChangeForFilter}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={() => {
          openDrawerWithFilter(filter.name)
        }}
      />
    )
  }

  // Helper function to render a radio filter
  const renderRadioFilter = (filter: FilterItem, isDrawer = false) => {
    const { filterName, currentValue, options } = prepareRadioFilterData(
      filter,
      selectedFiltersState,
      showGroupedPage,
      routerAsPath,
      typedSubCollectionCards,
    )

    const handleRadioChange = (value: string) => {
      handleRadioFilterChange(routerContext, { ...commonParams, value, options, filter })
    }

    if (isDrawer) {
      return (
        <RadioButtonGroup
          key={`drawer-${filter.name}-${selectedFiltersState[filterName]?.length}`}
          options={options.map(opt => ({
            label: opt.label,
            value: opt.id,
            checked: opt.checked,
          }))}
          onChange={handleRadioChange}
          name={filter.name}
          className={styles.radioGroup}
          value={options.find(opt => opt.name === currentValue || opt.label === currentValue)?.id}
          itemsClassName={styles.radioItems}
          radioItemClassName={styles.radioItem}
          radioLabelClassName={styles.radioLabel}
        />
      )
    }

    // Format the label for the radio filter
    const formatRadioFilterLabel = () => {
      const selectedCount = getSelectedCount(filter, selectedFiltersState, showGroupedPage)
      const singleSelectedValue = selectedCount === 1 ? currentValue : undefined

      const displayName = singleSelectedValue
        ? getDisplayNameForFilterValue({
            filters: serverFilters,
            filterName: filter.name,
            filterValue: singleSelectedValue,
          })
        : undefined
      return formatFilterLabel(filter.config.label, selectedCount, displayName)
    }

    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.CHECKBOX}
        label={formatRadioFilterLabel()}
        options={options}
        value={options.find(opt => opt.name === currentValue || opt.label === currentValue)?.id}
        isActive={currentValue !== ''}
        onChange={selectedOptions => {
          if (selectedOptions.length > 0) {
            handleRadioChange(selectedOptions[0].id)
          }
        }}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={() => {
          openDrawerWithFilter(filter.name)
        }}
      />
    )
  }

  // Helper function to render a chip filter
  const renderChipFilter = (
    filter: FilterItem,
    selectedValues: string[],
    onChange: (selectedOptions: CheckboxOption[]) => void,
    isDrawer = false,
  ) => {
    const selectedCount = getSelectedCount(filter, selectedFiltersState, showGroupedPage)

    // Convert values to chip options
    const chipOptions = filter.values.map(value => {
      const valueStr = typeof value === 'string' ? value : value.name
      const displayName = typeof value === 'string' ? value : (value.displayName ?? value.name)
      const isSelected = selectedValues.includes(valueStr)

      return {
        label: displayName,
        value: valueStr,
        closeable: false,
        key: valueStr,
        active: isSelected,
        className: isSelected ? styles.chipItemActive : styles.chipItem,
      }
    })

    const handleChipToggle = (value: string) => {
      const isSelected = selectedValues.includes(value)
      const newSelectedValues = isSelected ? selectedValues.filter(v => v !== value) : [...selectedValues, value]

      const selectedOptions = chipOptions
        .filter(option => newSelectedValues.includes(option.value))
        .map(option => ({
          label: option.label,
          value: option.value,
          id: option.value,
          name: option.value,
          checked: true,
        }))

      onChange(selectedOptions as CheckboxOption[])
    }

    if (isDrawer) {
      // Custom chip rendering to ensure active state is maintained
      return (
        <div className={styles.chipFilterGroup}>
          {filter.values.map(value => {
            const valueStr = typeof value === 'string' ? value : value.name
            const displayName = typeof value === 'string' ? value : (value.displayName ?? value.name)
            const isSelected = selectedValues.includes(valueStr)

            return (
              <div
                key={valueStr}
                className={isSelected ? styles.chipItemActive : styles.chipItem}
                onClick={() => {
                  handleChipToggle(valueStr)
                }}
                role="button"
                aria-pressed={isSelected}
                tabIndex={0}
              >
                {displayName}
              </div>
            )
          })}
        </div>
      )
    }

    // Format the label for the chip filter
    const formatChipFilterLabel = () => {
      let singleSelectedValue: string | undefined
      if (selectedCount === 1 && selectedValues.length === 1) {
        const selectedValue = selectedValues[0]
        if (selectedValue) {
          singleSelectedValue = getDisplayNameForFilterValue({
            filters: serverFilters,
            filterName: filter.name,
            filterValue: selectedValue,
          })
        }
      }
      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }

    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.CHECKBOX}
        label={formatChipFilterLabel()}
        options={createCheckboxOptions(filter.values, selectedValues, filter.name)}
        isActive={selectedCount > 0}
        onChange={onChange}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={() => {
          openDrawerWithFilter(filter.name)
        }}
      />
    )
  }

  // Unified function to render filter component
  const renderFilterComponent = (filter: FilterItem, isDrawer = false) => {
    const { config, name } = filter
    const filterType = config.type.toLowerCase()
    const filterName = name.toLowerCase()
    const state = selectedFiltersState[filterName] ?? []

    switch (filterType) {
      case FILTER_TYPES.CARD:
        return renderRadioFilter(filter, isDrawer)

      case FILTER_TYPES.CHECKBOX:
        if (filterName === EFilterKeys.COLORS) {
          return renderColorFilter(filter, isDrawer)
        }
        if (filterName === EFilterKeys.PRODUCT_TYPE) {
          return renderCheckboxFilter(
            filter,
            createCheckboxOptions(filter.values, state, filter.name),
            selectedOptions => {
              handleCheckboxFilterChange(filter, selectedOptions)
            },
            isDrawer,
          )
        }
        // For all other filters, use chips
        return renderChipFilter(
          filter,
          state,
          selectedOptions => {
            handleCheckboxFilterChange(filter, selectedOptions)
          },
          isDrawer,
        )

      default:
        return null
    }
  }

  // Handle sort change and close drawer if on mobile
  const handleSortChange = (value: string) => {
    updateQuery({ sort: { type: 'toggle', value } })
    isSortOptionSelected.current = true
    closeSortDrawer()
  }

  // Helper function to get capitalized filter text
  const getCapitalizedFilterText = (): string => {
    return t(FILTER_TEXT).charAt(0).toUpperCase() + t(FILTER_TEXT).slice(1)
  }

  // Helper function for formatting filter labels
  const formatFilterLabel = (
    filterLabel: string,
    selectedCount: number,
    singleSelectedValue?: string,
  ): React.ReactNode => {
    if (selectedCount === 1 && singleSelectedValue) {
      return `${toTitleCase(filterLabel)}: ${singleSelectedValue}`
    }
    return (
      <>
        {toTitleCase(filterLabel)}
        {selectedCount > 0 && ` (${selectedCount})`}
      </>
    )
  }

  // Render only sort functionality when no filters exist
  const hasFilters = filters.length > 0

  // Render filter with sticky behavior
  const renderFilter = (isIntersecting: boolean) => {
    const isSticky = !isIntersecting
    const showStickyWithHeader = isSticky && !isScrollingDown
    return (
      <>
        {isSticky && <div className={styles.filterSpacer} />}
        <div
          ref={containerRef}
          className={classNames(styles.filtersContainer, className, {
            [`${styles.filtersContainerSticky}`]: isSticky,
            // Experiment: apply transition ALWAYS when sticky so changes in top position are smooth both ways
            [`${styles.filtersContainerStickyWithHeader}`]: isSticky && shouldApplyTransitions,
            // Control: use master class without transitions only when not scrolling down
            [`${styles.filtersContainerStickyWithHeaderControl}`]: showStickyWithHeader && !shouldApplyTransitions,
            [`${styles.hiddenDuringSearch}`]: isSearchMode,
          })}
          style={isSticky ? stickyStyle : undefined}
          data-testid="filters-container"
        >
          <div className={styles.filtersWrapper}>
            <div className={styles.filtersLeft}>
              {hasFilters && (
                <button
                  className={classNames(
                    styles.filterButton,
                    effectiveTotalSelectedFilters > 0 && styles.filterButtonActive,
                  )}
                  onClick={handleMainFilterClick}
                  data-testid={`${FILTER_DRAWER_BUTTON_TEST_ID}-${effectiveTotalSelectedFilters}`}
                >
                  <Icon iconSvg={FilterInlineIcon} className={styles.filterIcon} />
                  {getCapitalizedFilterText()}{' '}
                  {effectiveTotalSelectedFilters > 0 && `(${effectiveTotalSelectedFilters})`}
                </button>
              )}

              {/* Use FilterDropdown for sort with isMobile=true */}
              {!skipSorting && (
                <FilterDropdown
                  type={FILTER_DROPDOWN_TYPES.SORT}
                  label={
                    <span>
                      {t(SORT_BY_TEXT)}
                      {sortBy !== '' && isSortOptionSelected.current
                        ? `: ${SORT_OPTIONS_CLP_EXPERIMENT.find(opt => opt.value === sortBy)?.labelKey ? t(SORT_OPTIONS_CLP_EXPERIMENT.find(opt => opt.value === sortBy)?.labelKey ?? '') : SORT_OPTIONS_CLP_EXPERIMENT.find(opt => opt.value === sortBy)?.label}`
                        : ''}
                    </span>
                  }
                  value={sortBy}
                  onChange={handleSortChange}
                  placement="bottom"
                  buttonClassName={`${styles.mobileFilterButton} ${styles.mobileSortButton}`}
                  menuClassName={styles.sortDropdownMenu}
                  onTriggerClick={openSortDrawer}
                  isMobile={true}
                  isActive={sortBy !== ''}
                  data-testid={SORT_TRIGGER_TEST_ID}
                />
              )}

              {hasFilters &&
                filters.map(filter => (
                  <div key={filter.name} className={styles.filterItem}>
                    {renderFilterComponent(filter)}
                  </div>
                ))}
            </div>

            {/* Use FilterDrawer with isMobile=true and portal */}
            {hasFilters && (
              <FilterDrawer
                placement="bottom"
                open={isDrawerOpen}
                onClose={closeDrawer}
                selectedFilterChips={selectedFilterChips}
                onChipDismiss={handleChipDismissByLabel}
                clearAllFilters={clearAllFilters}
                totalSelectedFilters={effectiveTotalSelectedFilters}
                noOfItems={noOfItems}
                isMobile={true}
                height={'80vh'}
                clearAllButtonTestId={CLEAR_ALL_BUTTON_TEST_ID}
                chipTestIdPrefix={FILTER_CHIP_PREFIX}
                maskClosable={false} // Disable click-outside-to-close for mobile only
                zIndex={1010} // Ensure overlay appears above header (header sticky has z-index: 1002)
                usePortal={true} // Render via portal to escape stacking context
              >
                <FilterCollapseGroup
                  filters={filters}
                  selectedFiltersState={selectedFiltersState}
                  activeItems={collapseActiveKeys}
                  onChange={handleCollapseChange}
                  renderFilterComponent={renderFilterComponent}
                  isMobile={true}
                />
              </FilterDrawer>
            )}

            {/* Use FilterDrawer for sort drawer with portal */}
            <FilterDrawer
              placement="bottom"
              open={isSortDrawerOpen}
              onClose={closeSortDrawer}
              title={t(SORT_BY_TEXT)}
              height={'auto'}
              totalSelectedFilters={0}
              customFooter={null}
              isMobile={true}
              hideFooter={true}
              headerClassName={styles.sortDrawerHeader}
              data-testid={SORT_DRAWER_TITLE_TEST_ID}
              zIndex={1010} // Ensure overlay appears above header (header sticky has z-index: 1002)
              usePortal={true} // Render via portal to escape stacking context
            >
              <div className={styles.sortDrawerContent}>
                <RadioButtonGroup
                  name={SORT_OPTIONS_MOBILE_DRAWER_NAME}
                  options={SORT_OPTIONS_CLP_EXPERIMENT.map(option => ({
                    value: option.value,
                    label: option.labelKey ? t(option.labelKey) : option.label,
                  }))}
                  value={sortBy}
                  onChange={handleSortChange}
                  className={styles.sortRadioGroup}
                  itemsClassName={styles.sortRadioItems}
                  radioItemClassName={styles.sortRadioItem}
                  radioLabelClassName={styles.sortRadioLabel}
                />
              </div>
            </FilterDrawer>
          </div>
        </div>
      </>
    )
  }

  return (
    <div id="stickyWrapperMobileFilters" ref={wrapperRef}>
      <StickyWrapper renderChildren={renderFilter} />
    </div>
  )
}
